import { useMemo, type ReactNode } from 'react';
import { Box, ThemeProvider, useTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ar, bn, enUS, hi, type Locale } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { APP_SHAPE } from '../constants/colors';
import { createContentTheme } from './createContentTheme';

/** Date-picker month/day names follow the UI language where date-fns ships a locale. */
const DATE_LOCALES: Record<string, Locale> = { ar, bn, hi };

interface ContentThemeProviderProps {
  children: ReactNode;
}

/**
 * Applies the ESG content theme (RECYCLEE blue / slate / #F1F5F9 canvas, Readex Pro
 * type scale, flat surfaces) to everything rendered inside the shell tile.
 *
 * Mode and direction are read from the shell theme so the dark-mode toggle and
 * RTL switch keep working; the shell itself (sidebar, app bar) never sees this
 * theme. Portals opened from the page (dialogs, menus, date pickers, tooltips)
 * inherit it through React context.
 */
export function ContentThemeProvider({ children }: ContentThemeProviderProps) {
  const shellTheme = useTheme();
  const { i18n } = useTranslation();
  const mode = shellTheme.palette.mode;
  const direction = shellTheme.direction;
  const latin = (i18n.language || 'en').startsWith('en');

  const theme = useMemo(
    () => createContentTheme(mode, direction, { latin }),
    [mode, direction, latin],
  );

  const dateLocale = DATE_LOCALES[(i18n.language || 'en').split('-')[0]] ?? enUS;

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
        <ContentSurface>{children}</ContentSurface>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

/**
 * Warm full-bleed canvas painted over the shell tile (which keeps its own
 * 24px padding and 16px corners). No transform / filter / contain here: the
 * Reports page mounts its PDF capture roots `position: fixed` off-screen and
 * any of those would change their containing block.
 */
function ContentSurface({ children }: { children: ReactNode }) {
  return (
    <Box
      className="esg-content-surface"
      sx={{
        m: -3,
        px: { xs: 2, sm: 3, lg: 4 },
        py: { xs: 2.5, sm: 3, lg: 4 },
        borderRadius: `${APP_SHAPE.radiusLg}px`,
        bgcolor: 'background.default',
        color: 'text.primary',
        minHeight: { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 32px)' },
        transition: 'background-color 200ms ease',
      }}
    >
      <Box sx={{ maxWidth: 1600, mx: 'auto', minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
